/**
 * Public Properties API
 * RTK Query endpoints for public property listing and details
 */

import type { Address, Property, PropertyFilters, PropertyType, PropertyStatus } from "../../types";
import { baseApi } from "./baseApi";

/**
 * Normalize backend property response to ensure consistent types
 * No transformation - use snake_case directly from backend
 */
const normalizeProperty = (backendProperty: any): Property => {
	// Parse numeric fields that might come as strings from backend
	const parseNumber = (value: any): number | undefined => {
		if (value === null || value === undefined) return undefined;
		if (typeof value === "number") return value;
		if (typeof value === "string") {
			const parsed = parseFloat(value);
			return isNaN(parsed) ? undefined : parsed;
		}
		return undefined;
	};

	// Return property with snake_case fields matching backend exactly
	return {
		id: backendProperty.id || "",
		name: backendProperty.name || "",
		description: backendProperty.description || "",
		property_type: (backendProperty.property_type || "apartment") as PropertyType,
		status: (backendProperty.status || "available") as PropertyStatus,
		address: backendProperty.address || "",
		city: backendProperty.city,
		state: backendProperty.state,
		zip_code: backendProperty.zip_code,
		unit_number: backendProperty.unit_number,
		country: backendProperty.country || "Nigeria",
		bedrooms: backendProperty.bedrooms ?? 0,
		bathrooms: parseNumber(backendProperty.bathrooms) ?? 0,
		square_feet: backendProperty.square_feet ?? 0,
		monthly_rent: parseNumber(backendProperty.monthly_rent),
		rent_amount: parseNumber(backendProperty.rent_amount),
		rent_type: backendProperty.rent_type,
		initial_payment_duration: backendProperty.initial_payment_duration,
		security_deposit: parseNumber(backendProperty.security_deposit),
		purchase_price: parseNumber(backendProperty.purchase_price),
		images: Array.isArray(backendProperty.images) ? backendProperty.images : [],
		features: Array.isArray(backendProperty.features)
			? backendProperty.features.filter(
					(f: any) => f && String(f).trim().length > 0,
			  )
			: [],
		organization: backendProperty.organization,
		created_at: backendProperty.created_at || new Date().toISOString(),
		updated_at: backendProperty.updated_at || new Date().toISOString(),
	};
};

/**
 * Query parameters for property listing
 */
export interface PropertyListParams extends PropertyFilters {
	page?: number;
	page_size?: number;
	search?: string;
	ordering?: string;
}

/**
 * Response format for property listing
 */
export interface PropertyListResponse {
	count: number;
	next: string | null;
	previous: string | null;
	results: Property[];
}

/**
 * Public properties API endpoints
 */
export const publicPropertiesApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		/**
		 * Get list of public properties with filters
		 * No authentication required
		 */
		getPublicProperties: builder.query<
			PropertyListResponse,
			PropertyListParams | void
		>({
			query: (params) => {
				const queryParams = new URLSearchParams();

				// Add filters to query params
				if (params) {
					if (params.property_type) queryParams.append("property_type", params.property_type);
					if (params.status) queryParams.append("status", params.status);
					if (params.city) queryParams.append("city", params.city);
					if (params.state) queryParams.append("state", params.state);
					if (params.min_rent)
						queryParams.append("min_rent", params.min_rent.toString());
					if (params.max_rent)
						queryParams.append("max_rent", params.max_rent.toString());
					if (params.bedrooms)
						queryParams.append("bedrooms", params.bedrooms.toString());
					if (params.page) queryParams.append("page", params.page.toString());
					if (params.page_size)
						queryParams.append("page_size", params.page_size.toString());
					if (params.search) queryParams.append("search", params.search);
					if (params.ordering) queryParams.append("ordering", params.ordering);
				}

				// Build URL with query string (even if empty)
				const queryString = queryParams.toString();
				const url = queryString
					? `/public/properties/?${queryString}`
					: "/public/properties/";

				return {
					url,
					method: "GET",
				};
			},
			transformResponse: (response: any) => ({
				...response,
				results: response.results?.map(normalizeProperty) || [],
			}),
			providesTags: (result) =>
				result
					? [
							...result.results.map(({ id }) => ({
								type: "Properties" as const,
								id,
							})),
							{ type: "Properties", id: "LIST" },
					  ]
					: [{ type: "Properties", id: "LIST" }],
		}),

		/**
		 * Get public property details by ID
		 * No authentication required
		 */
		getPublicPropertyById: builder.query<Property, string>({
			query: (id) => ({
				url: `/public/properties/${id}`,
				method: "GET",
			}),
			transformResponse: normalizeProperty,
			providesTags: (result, error, id) => [{ type: "Property", id }],
		}),
	}),
});

/**
 * Export hooks for usage in functional components
 */
export const {
	useGetPublicPropertiesQuery,
	useGetPublicPropertyByIdQuery,
	useLazyGetPublicPropertiesQuery,
	useLazyGetPublicPropertyByIdQuery,
} = publicPropertiesApi;
